<template>
  <div>
    <div v-if="hasAccess">
      <div v-if="!userTable" class="login-message">
        <div class="login-table">
          <div class="pre-entry-table">
            <div class="header">
              <div class="header-cont">
                <div class="table-icon"><i class="ar-icon icon-ico_table_users"></i></div>
              </div>
            </div>
          </div>
          <div class="wating-msg">One moment while we find your {{ groupTerminology.toLowerCase() }}...</div>
          <div class="joined">
            <div class="join-label" style="text-align: center">
              <i class="fa fa-spinner fa-pulse fa-2x fa-fw"></i>
            </div>
          </div>
        </div>
      </div>
      <div v-if="userTable">
        <div class="login-form text-center">
          <router-link v-if="canJoinEvent" :to="{ name: ROUTES.DIGITAL_EVENT_ROOM_LIST }" v-slot="{ href, navigate }">
            <a
              :href="href"
              :disabled="!tableUsers"
              @click="
                updateUserFirstLogin();
                navigate;
              "
              class="btn btn-lg btn-primary"
            >
              Enter <i class="fa fa-fw fa-lg fa-arrow-right"></i>
            </a>
          </router-link>
          <p class="text-muted" v-else>
            We have verified your access, <br />
            please return at the event time
          </p>
        </div>
        <div v-if="isPublicGallery" class="login-message public">
          <div class="login-table">
            <ul class="public-email-message">
              <li>We notice that you are logging in with a public email address.</li>
              <li>You will be placed in the public gallery with reduced event features.</li>
            </ul>
            <div v-if="event.EVENT_ACCESS_TYPE === 'ticket'" class="public-email-ticket-option">
              <p>
                If you would like to change your email address please update your ticket information, logout and log
                back in:
              </p>
              <p class="text-center">
                <a href="../private/event-attendance" class="btn btn-lg btn-primary">Edit my ticket email address</a>
              </p>
            </div>
            <div v-if="event.EVENT_ACCESS_TYPE === 'account'" class="public-email-account-option">
              <p>
                If you would like to change your email address you can edit your profile or create a new account:
              </p>
              <p class="text-center">
                <a href="../private/edit-profile" class="btn btn-lg btn-primary">Edit my profile</a>
                <a href="javascript:void(0);" @click="logout" class="btn btn-lg btn-primary">Create a new account</a>
              </p>
            </div>
            <p class="public-email-message-common">
              Using your company email address will allow us to place you on your own organisation
              {{ groupTerminology.toLowerCase() }} with your colleagues. This will give you access to private chat and
              other features.
            </p>
          </div>
        </div>
        <div v-else class="login-message nonpublic">
          <div class="login-table clearfix">
            <div class="pre-entry-table">
              <div class="header">
                <div class="header-cont">
                  <div class="table-icon"><i class="ar-icon icon-ico_table_users"></i></div>
                </div>
              </div>
            </div>

            <div class="table-cont">
              <div class="table-name">{{ userTable.TITLE }}</div>
              <template v-if="tableUsers">
                <div v-if="!tableUsers.length" class="joined">
                  <div class="join-label-first">
                    Welcome {{ firstName }}, we've placed you on the {{ userTable.TITLE }}
                    {{ groupTerminology.toLowerCase() }}. <br />You're the first to arrive!
                  </div>
                </div>
                <div v-if="tableUsers.length" class="joined">
                  <div class="join-label">
                    {{ firstName }}, we've placed you on the {{ userTable.TITLE }} {{ groupTerminology.toLowerCase() }}.
                    You'll be joining:
                  </div>
                  <div class="my-table org-table">
                    <div class="guests">
                      <ul class="guest-list">
                        <li class="guest" v-for="user in tableUsers" :key="'tableInfoUser-' + user.userUUID">
                          <person
                            :person="user"
                            :options="{ classes: 'in-columns preentry', hideAdminIcon: true }"
                          ></person>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="joined">
                  <div class="join-label" style="text-align: center">
                    <i class="fa fa-spinner fa-pulse fa-2x fa-fw"></i>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="loading" class="joined">
      <div class="join-label" style="text-align: center">
        <i class="fa fa-spinner fa-pulse fa-2x fa-fw"></i>
      </div>
    </div>
    <div v-else>
      <div v-if="event.NO_TICKET_MESSAGE" v-html="event.NO_TICKET_MESSAGE" class="message-noticket custom"></div>
      <div v-else class="message-noticket">
        This event is for ticket holders only. If you have a ticket please ensure you login with the email address
        associated with that ticket.
      </div>
    </div>
    <div class="other-email pull-left">
      <a href="javascript:void(0);" @click="logout">
        <i class="fa fa-fw fa-arrow-left"></i>&nbsp;Use different email address
      </a>
    </div>
  </div>
</template>

<script>
import AuthMixin from "./mixins/AuthMixin";
import PermissionMixin from "../mixins/Permissions/PermissionsMixin";
import Person from "@/components/DigitalEvent/RoomList/Room/People/Person";
import { fieldComparator } from "@/helpers/helpers";
import { mapGetters, mapState } from "vuex";

export default {
  name: "TableInfo",
  mixins: [AuthMixin, PermissionMixin],
  components: {
    person: Person
  },
  data() {
    return {
      userTable: null,
      tableUsers: null,
      loading: true
    };
  },
  computed: {
    ...mapState({
      event: state => state.event.context
    }),
    ...mapGetters({
      groupTerminology: "room/groupTerminology",
      isPublicGallery: "tables/isPublicGallery"
    })
  },
  methods: {
    async updateUserFirstLogin() {
      await this.$store.commit("users/setUserFirstLogin", false);
    }
  },
  async created() {
    // Load user and DigitalEvent CT context
    await this.$store.dispatch("loadInitData", { roomSlug: null });

    if (!this.hasAccess) {
      this.$store.commit("restrictAccess");
      this.loading = false;
      return;
    }

    this.userTable = await this.$store.dispatch("tables/getOrCreateUserTable");

    let { users } = await this.$store.dispatch("tables/getTableState");
    let filteredUser = users.filter(u => u.userUUID !== this.user.user_uuid);
    this.tableUsers = [...filteredUser].sort(fieldComparator(["firstName", "lastName"]));
    this.loading = false;
  }
};
</script>
